import React, { useEffect, useRef } from 'react';
import './style.css';

// hooks
import {
    useIsWhite,
    useMoveHistory,
    useMyBombs,
    useBombPlantingTime,
    useBombTimerSyncedAt,
    useGameState
} from '../../hooks';

// components
import { Timer } from '../';

// constants
import { GAME_STATES } from '../../constants';

const SidePanel = ({
    viewIndex,
    onGoToStart,
    onGoBack,
    onGoForward,
    onGoToLatest,
    onGoToMove,
    onResign,
    onOfferDraw,
    drawCooldown = 0,
    onRequestRematch,
    onNewGame,
    rematchRequested,
    rematchDeclinedMsg,
    clickToMove = false,
    onClickToMoveToggle,
    onAbort,
    mobileTab = null,
}) => {
    const moveHistory = useMoveHistory();
    const isWhite = useIsWhite();
    const myBombs = useMyBombs();
    const bombPlantingTimeLeft = useBombPlantingTime();
    const bombTimerSyncedAt = useBombTimerSyncedAt();
    const gameState = useGameState();

    // On mobile, show move/bomb content on 'moves' tab (or always on desktop).
    // Show settings only on 'settings' tab (or always on desktop).
    const showMain = !mobileTab || mobileTab === 'moves';
    const showSettings = !mobileTab || mobileTab === 'settings';

    const moveHistoryEndRef = useRef(null);
    useEffect(() => {
        if (viewIndex >= moveHistory.length) {
            moveHistoryEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [moveHistory, viewIndex]);

    return (
        <div className="side-panel">
            {showMain && (
                <>
                    {(gameState === GAME_STATES.placing_bombs) ?
                        <div className="bomb-placement-info">
                            <div className="bpi-header">
                                {myBombs.length < 3 ? "Plant your surprises." : "Traps set."}
                            </div>
                            <div className="bpi-subtext">
                                {myBombs.length < 3 ? "They won't see it coming." : '\u00a0'}
                            </div>
                            <div className="bpi-timer-wrap">
                                <Timer
                                    isActive={true}
                                    serverSeconds={bombPlantingTimeLeft}
                                    lastSyncAt={bombTimerSyncedAt}
                                />
                            </div>
                            <div className="bpi-slots">
                                {[0, 1, 2].map(i => (
                                    <div key={i} className={`bpi-slot${i < myBombs.length ? ' bpi-slot--placed' : ''}`}>
                                        {i < myBombs.length ? '💣' : ''}
                                    </div>
                                ))}
                            </div>
                            <div className="bpi-count">{myBombs.length} of 3 placed</div>
                            {myBombs.length === 3
                                ? <div className="bpi-waiting">Waiting for your opponent...</div>
                                : <p className="bpi-rule">{isWhite ? "Ranks 3 & 4" : "Ranks 5 & 6"} only.</p>
                            }
                            {onAbort && (
                                <button className="action-btn action-btn--danger bpi-abort" onClick={onAbort}>
                                    Abort
                                </button>
                            )}
                        </div>
                        :
                        <div className="move-history">
                            <div className="move-history-header">
                                <span className="moves-label">Moves</span>
                                <div className="nav-controls">
                                    <button className="nav-btn" onClick={onGoToStart} title="Start">⏮</button>
                                    <button className="nav-btn" onClick={onGoBack} title="Previous">◀</button>
                                    <button className="nav-btn" onClick={onGoForward} title="Next">▶</button>
                                    <button className="nav-btn" onClick={onGoToLatest} title="Latest">⏭</button>
                                </div>
                            </div>
                            <div className="move-history-table">
                                {Array.from({ length: Math.ceil(moveHistory.length / 2) }).map((_, idx) => {
                                    const whiteMoveIdx = 2 * idx + 1;
                                    const blackMoveIdx = 2 * idx + 2;
                                    return (
                                        <div className="move-history-row" key={idx}>
                                            <div className="move-turn">{idx + 1}.</div>
                                            <div
                                                className={`move-white${viewIndex === whiteMoveIdx ? ' move-active' : ''}`}
                                                onClick={() => moveHistory[2 * idx] && onGoToMove(whiteMoveIdx)}
                                            >
                                                {moveHistory[2 * idx] || ''}
                                            </div>
                                            <div
                                                className={`move-black${viewIndex === blackMoveIdx ? ' move-active' : ''}`}
                                                onClick={() => moveHistory[2 * idx + 1] && onGoToMove(blackMoveIdx)}
                                            >
                                                {moveHistory[2 * idx + 1] || ''}
                                            </div>
                                        </div>
                                    );
                                })}
                                <div ref={moveHistoryEndRef} />
                            </div>
                        </div>
                    }
                    {(onResign || onOfferDraw) && (
                        <div className="game-actions">
                            {onResign && (
                                <button className="action-btn action-btn--danger" onClick={onResign}>
                                    Resign
                                </button>
                            )}
                            {onOfferDraw && (
                                <button
                                    className="action-btn action-btn--ghost"
                                    onClick={onOfferDraw}
                                    disabled={drawCooldown > 0}
                                >
                                    {drawCooldown > 0 ? `Draw (${drawCooldown}s)` : 'Draw'}
                                </button>
                            )}
                        </div>
                    )}
                    {(onRequestRematch || onNewGame) && (
                        <div className="game-actions">
                            {onRequestRematch && (
                                <button
                                    className={`action-btn action-btn--primary${rematchRequested ? ' action-btn--waiting' : ''}`}
                                    onClick={!rematchDeclinedMsg ? onRequestRematch : undefined}
                                    disabled={rematchRequested || !!rematchDeclinedMsg}
                                >
                                    {rematchDeclinedMsg ? rematchDeclinedMsg : rematchRequested ? 'Waiting...' : 'Rematch'}
                                </button>
                            )}
                            {onNewGame && (
                                <button className="action-btn action-btn--ghost" onClick={onNewGame}>
                                    New Game
                                </button>
                            )}
                        </div>
                    )}
                </>
            )}
            {showSettings && (
                <div className="settings-zone">
                    <div className="settings-label">Settings</div>
                    <div className="setting-row">
                        <span className="setting-name">Click to move</span>
                        <button
                            className={`toggle-btn${clickToMove ? ' toggle-btn--on' : ''}`}
                            onClick={onClickToMoveToggle}
                            role="switch"
                            aria-checked={clickToMove}
                            aria-label="Click to move"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default SidePanel;